'use client'

import { apiCall } from '@/lib/api/client'
import type { ActionResult } from '@/types/actions'
import type { Project } from '@/types/database'
import type { BrandFactKey } from '@/lib/project/constants'
import type { WizardData } from '@/types/forms'

export async function createProject(
  data: WizardData
): Promise<ActionResult<{ projectId: string }>> {
  return apiCall<{ projectId: string }>('/api/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateProject(
  data: Partial<
    Pick<
      Project,
      | 'domain'
      | 'brand_name'
      | 'brand_aliases'
      | 'market'
      | 'brand_facts'
      | 'brand_brief'
      | 'report_note'
      // Onboarding v3. Both are real columns on `projects` and both are in
      // `updateProjectSchema`; omitting them here made the wrapper reject at
      // the type level what the route would happily have accepted.
      | 'own_domains'
      | 'onboarding_step'
    >
  > & {
    competitors?: Array<{ domain: string; name: string; aliases?: string[] }>
  }
): Promise<ActionResult<Project>> {
  return apiCall<Project>('/api/projects/current', {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteProject(): Promise<ActionResult> {
  return apiCall('/api/projects/current', { method: 'DELETE' })
}

export async function switchProject(projectId: string): Promise<ActionResult> {
  return apiCall('/api/projects/switch', {
    method: 'POST',
    body: JSON.stringify({ projectId }),
  })
}

export async function extractBrandFacts(): Promise<
  ActionResult<{ brandFacts: import('@/types/database').BrandFact[]; brandBrief: string }>
> {
  return apiCall<{ brandFacts: import('@/types/database').BrandFact[]; brandBrief: string }>(
    '/api/brand-facts/extract',
    {
      method: 'POST',
      // Route declares maxDuration = 300 (REL-32); wait slightly longer than the platform does.
      timeout: 305_000,
    }
  )
}

/** Settings → Brand facts' save — a whole-array write that works for a manager too (R6.2a B3). */
export async function saveBrandFacts(
  data: Pick<Project, 'brand_facts' | 'brand_brief'>
): Promise<ActionResult<{ success: boolean }>> {
  return apiCall<{ success: boolean }>('/api/brand-facts', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/** "Confirm this is right" — marks one auto-detected brand fact as confirmed (R6.2a B3). */
export async function confirmBrandFact(
  key: BrandFactKey
): Promise<ActionResult<{ success: boolean }>> {
  return apiCall<{ success: boolean }>('/api/brand-facts/confirm', {
    method: 'POST',
    body: JSON.stringify({ key }),
  })
}

export async function savePromptsBatch(
  prompts: Array<{ text: string; source: 'suggested' | 'manual' }>
): Promise<ActionResult<{ saved: number }>> {
  return apiCall<{ saved: number }>('/api/prompts/batch', {
    method: 'POST',
    body: JSON.stringify({ prompts }),
    timeout: 55_000,
  })
}
